import { makeStyles, Theme } from '@material-ui/core/styles';
import { THEME_MODES } from 'src/theming';
import { LIGHT_HECTOR_THEME_COLORS } from 'src/theming/themes/hector/hector-theme-colors';
import { isCeatInstance, isHectorInstance } from 'src/environment';

export const getBackgroundGradient = (isLight: boolean, theme: Theme): string => {
    if (isHectorInstance) {
        return isLight
            ? 'transparent linear-gradient(180deg, #6635DF 0%, #8771BE 100%) 0% 0% no-repeat padding-box'
            : 'transparent linear-gradient(180deg, #6635DF 0%, #403164 100%) 0% 0% no-repeat padding-box';
    }
    if (isCeatInstance) {
        return `transparent linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%) 0% 0% no-repeat padding-box`;
    }
    return '';
};

export const useContractDashboardHectorStyle = makeStyles((theme: Theme) => {
    const isLightMode = theme.palette.type === THEME_MODES.LIGHT;

    return {
        dashboardContainer: {
            flexDirection: 'column',
            height: '100%',
        },
        toolBar: {
            height: 110,
            alignItems: 'center',
            flexWrap: 'nowrap',
            padding: `0 140px`,
            background: getBackgroundGradient(
                theme.palette.type === THEME_MODES.LIGHT,
                theme
            ),
            [theme.breakpoints.down('lg')]: {
                padding: `0 ${theme.spacing(1)}px`,
            },
            [theme.breakpoints.down('md')]: {
                height: 80,
            },
        },
        dragContractType: {
            '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.info.light}, ${theme.palette.info.light} 25px, ${theme.palette.secondary.main} 25px, ${theme.palette.secondary.main} 50px)`,
                border: `2px dashed ${theme.palette.info.main}`,
                zIndex: 1000,
            },
        },
        searchIcon: {
            width: 24,
            height: 24,
            opacity: 0.6,
        },
        cardData: {
            width: '60%',
        },
        quickActionsButton: {
            minWidth: 300,
            marginRight: theme.spacing(4),
            marginBottom: theme.spacing(4),
            height: 40,
            padding: `0 ${theme.spacing(5)}px`,
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.primary.contrastText,
            transition: '0.2s',
            '&:hover': {
                backgroundColor: theme.palette.secondary.light,
                opacity: 0.8,
            },
        },
        showMoreLink: {
            display: 'inline-block',
            marginLeft: 'auto',
            marginRight: `${theme.spacing(1)}rem`,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(2),
            fontWeight: theme.typography.fontWeightMedium,
        },
        searchButton: {
            height: 50,
            width: 50,
            borderRadius: 0,
            backgroundColor: theme.palette.common.white,
            transition: '0.2s',
            '& .MuiSvgIcon-root': {
                width: 35,
                height: 35,
                color: theme.palette.secondary.main,
            },
            '&:hover': {
                opacity: 0.6,
                backgroundColor: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
                opacity: 0.6,
                color: theme.palette.secondary.contrastText,
            },
        },
        contractsDashboardContainer: {
            padding: `0 140px`,
            [theme.breakpoints.down('lg')]: {
                padding: `0 ${theme.spacing(1)}px`,
            },
        },
        cardsContainer: {
            width: '30%',
            paddingRight: theme.spacing(4),
            opacity: ({ editMode }: { editMode: boolean; isDarkMode: boolean }) =>
                editMode ? 0.4 : 1,
        },
        chartsContainer: {
            width: '70%',
            paddingLeft: theme.spacing(4),
            opacity: ({ editMode }: { editMode: boolean; isDarkMode: boolean }) =>
                editMode ? 0.4 : 1,
        },
        quickActionsContainer: {
            width: '30%',
            paddingLeft: 140,
            paddingRight: 140,
            paddingTop: theme.spacing(4),
            [theme.breakpoints.down('lg')]: {
                padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
            },
        },
        divisionsContainer: {
            width: '100%',
            paddingTop: theme.spacing(4),
        },
        card: {
            width: '100%',
            '&:not(:last-child)': {
                marginBottom: theme.spacing(6),
            },
        },
        chart: {
            width: '50%',
        },
        linearChart: {
            width: '100%',
        },
        dashboardSettingsContainer: {
            padding: `30px 140px`,
            [theme.breakpoints.down('lg')]: {
                padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
            },
        },
        printButton: {
            width: 50,
            height: 50,
            borderRadius: 0,
            backgroundColor: isLightMode
                ? theme.palette.secondary.main
                : 'rgba(0, 0, 0, 0)',
            color: theme.palette.secondary.contrastText,
            transition: '0.2s',
            border: isLightMode
                ? 'none'
                : `1px solid ${LIGHT_HECTOR_THEME_COLORS.SECONDARY_CONTRAST_TEXT}`,
            '&:hover': {
                opacity: 0.6,
                backgroundColor: theme.palette.secondary.main,
            },
            '& .MuiSvgIcon-root': {
                width: 35,
                height: 35,
            },
            [theme.breakpoints.down('md')]: {
                width: 40,
                height: 40,
            },
        },
        damageLinkButton: {
            width: 300,
            height: 40,
            padding: `0 ${theme.spacing(5)}px`,
            marginRight: theme.spacing(4),
            marginBottom: theme.spacing(4),
            backgroundColor: theme.palette.info.light,
            color: theme.palette.primary.contrastText,
            transition: '0.2s',
            '& .MuiTypography-root': {
                fontSize: '1.6rem',
            },
            '&:hover': {
                backgroundColor: theme.palette.info.light,
                opacity: 0.8,
            },
        },
        radioButtonLabel: {
            [theme.breakpoints.down('lg')]: {
                '& .MuiFormControlLabel-label': {
                    fontSize: '1.6rem',
                },
            },
            [theme.breakpoints.down('md')]: {
                '& .MuiFormControlLabel-label': {
                    fontSize: '1.4rem',
                },
            },
        },
        radioFormControl: {
            minWidth: 300,
            '& .MuiFormGroup-root': {
                flexWrap: 'nowrap',
            },
            [theme.breakpoints.down('md')]: {
                minWidth: 260,
            },
        },
        searchFieldContainer: {
            display: 'flex',
            flexWrap: 'nowrap',
            width: '30%',
            [theme.breakpoints.down('lg')]: {
                width: '25%',
                '& .MuiFormControl-root': {
                    marginRight: theme.spacing(1),
                },
            },
        },
    };
});

export const useFormControlYearHectorStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            '& .MuiInputBase-root': {
                borderRadius: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border:
                    theme.palette.type === THEME_MODES.LIGHT
                        ? `1px solid ${theme.palette.secondary.main}`
                        : `1px solid ${theme.palette.secondary.contrastText}`,
            },
        },
    };
});

export const useFormControlHectorStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            maxWidth: 373,
            margin: `0 ${theme.spacing(3)}px 0 0`,
            '& label': {
                fontSize: '2.4rem',
            },
        },
    };
});

export const useInputHectorStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            width: '100%',
            height: 50,
            backgroundColor: theme.palette.secondary.contrastText,
            borderRadius: 0,
        },
        focused: {
            '& fieldSet': {
                borderColor: `${theme.palette.secondary.main}!important`,
            },
        },
        input: {
            height: 50,
            boxSizing: 'border-box',
            color: theme.palette.secondary.main,
            '&::placeholder': {
                fontSize: '1.8rem',
            },
        },
    };
});

export const useFormControlLabelHectorStyles = makeStyles((theme: Theme) => {
    const isLightMode = THEME_MODES.LIGHT === theme.palette.type;

    return {
        root: {
            display: 'flex!important',
            alignItems: 'center',
            margin: 0,
            padding: `0!important`,
            '&>div': {
                width: '100%',
                '& .MuiFormControl-root': {
                    width: '100%',
                },
            },
        },
        label: {
            fontSize: '1.6rem',
            marginRight: theme.spacing(4),
            color: isLightMode
                ? theme.palette.secondary.main
                : theme.palette.secondary.contrastText,
        },
    };
});

export const useDashboardActionsHectorStyle = makeStyles((theme: Theme) => {
    const isLightMode = theme.palette.type === THEME_MODES.LIGHT;

    return {
        editModeButtons: {
            width: '50%',
            '&>button': {
                height: 50,
                padding: `0 ${theme.spacing(4)}px`,
                marginRight: theme.spacing(3),
                backgroundColor: isLightMode
                    ? theme.palette.secondary.main
                    : 'rgba(0, 0, 0, 0)',
                color: theme.palette.secondary.contrastText,
                borderRadius: 0,
                fontSize: '1.8rem',
                transition: '0.2s',
                border: isLightMode
                    ? 'none'
                    : `1px solid ${LIGHT_HECTOR_THEME_COLORS.SECONDARY_CONTRAST_TEXT}`,
                '&[data-mode=edit]': {
                    backgroundColor: theme.palette.secondary.main,
                    border: `1px solid ${theme.palette.grey.A100}`,
                },
                '&:hover': {
                    opacity: 0.6,
                    backgroundColor: theme.palette.secondary.main,
                },
                '& .MuiSvgIcon-root': {
                    width: 30,
                    height: 30,
                },
                [theme.breakpoints.down('md')]: {
                    height: 40,
                    fontSize: '1.6rem',
                },
            },
        },
        saveButton: {
            backgroundColor: theme.palette.secondary.main,
        },
    };
});

export const useHectorSelectStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            height: 50,
            lineHeight: 3,
            padding: `0 ${theme.spacing(2)}px`,
            fontSize: '1.4rem',
            [theme.breakpoints.down('md')]: {
                height: 40,
                padding: `0 ${theme.spacing(1)}px`,
            },
        },
        select: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            color: theme.palette.primary.contrastText,
            fontSize: '1.8rem',
            [theme.breakpoints.down('md')]: {
                fontSize: '1.6rem',
            },
        },
        icon: {
            width: 30,
            height: 30,
            top: 'calc(50% - 15px)',
            color:
                theme.palette.type === THEME_MODES.LIGHT
                    ? theme.palette.secondary.main
                    : theme.palette.secondary.contrastText,
            [theme.breakpoints.down('md')]: {
                width: 25,
                height: 25,
                top: 'calc(50% - 13px)',
            },
        },
        disabled: {
            color: theme.palette.primary.contrastText,
        },
    };
});

export const useHectorMenuStyles = makeStyles((theme: Theme) => {
    return {
        paper: {
            backgroundColor: theme.palette.primary.main,
        },
        list: {
            padding: 0,
            '& .MuiTypography-root': {
                fontSize: '1.6rem',
            },
        },
    };
});
